import {batch} from 'react-redux';
import _ from 'lodash';
import {
  CardType,
  CommonButtonLabel,
  NormalizationStep,
  NormalizationType,
  ProtocolEditorViewMode,
  TaskType,
  TrayType,
  WellIncrementPattern,
} from '../../constants/dataEnum';
import * as cardStateAction from '../actions/cardStateAction';
import * as normalizationStateAction from '../actions/normalizationStateAction';
import * as temporaryStateAction from '../actions/temporaryStateAction';
import * as protocolEditorAction from '../actions/protocolEditorAction';
import * as protocolManagementBusiness from './protocolManagementBusiness';
import * as labwareStateAction from '../actions/labwareStateAction';
import {convertRowToAlphabet, getNewCardName} from './commonTaskListBusiness';
import {find} from '../../lib/objectHelper';
import {hideLoading, showLoading} from '../actions/appStateAction';
import {initiateNormalizationWorker} from '../../workers/initiateWorker';
import {smarterDefaultDispenseTipPosition} from './taskListBusinessAction';
import {
  generateNormalizationPreviewData,
  is12WellTrough,
  is1WellTrough,
  is8WellTrough,
} from '../../lib/normalizationHelper';
import {globalAddUndoItem} from './undoRedoBusinessAction';
import {showModalSimplified} from './commonModalBusiness';
import {parseMessage} from '../../lib/stringHelper';

export const selectNormalizationSamplePlate = (labware) => (dispatch) => {
  batch(() => {
    dispatch(protocolEditorAction.setSelectedLabwareID(labware?.LabwareID ?? ''));
    dispatch(normalizationStateAction.setNormalizationSelectedLabware(labware));
  });
};
export const selectNormalizationDiluentPlate = (labware) => (dispatch) => {
  batch(() => {
    dispatch(protocolEditorAction.setSelectedLabwareID(labware?.LabwareID ?? ''));
    dispatch(normalizationStateAction.setNormalizationDiluentLabware(labware?.LabwareID ?? ''));
  });
};

export const cancelHandler = () => (dispatch) => {
  dispatch(normalizationStateAction.setCurrentWizardView(0));
  dispatch(normalizationStateAction.resetNormalizationState());
};

export const uploadNormalizationCSVFile = (rawData, parsedData, fileName) => (dispatch) => {
  batch(() => {
    dispatch(normalizationStateAction.setCsvRawData(rawData));
    dispatch(normalizationStateAction.setCsvData(parsedData.data));
    dispatch(normalizationStateAction.setCsvTotalRow(parsedData.data.length));
    dispatch(normalizationStateAction.setCsvFileName(fileName));
    dispatch(normalizationStateAction.setSelectedColumnIndex(null));
  });
};

export const removeNormalizationCSVFile = () => (dispatch) => {
  batch(() => {
    dispatch(normalizationStateAction.setCsvRawData(''));
    dispatch(normalizationStateAction.setCsvData([]));
    dispatch(normalizationStateAction.setCsvTotalRow(0));
    dispatch(normalizationStateAction.setCsvFileName(''));
    dispatch(normalizationStateAction.setSelectedColumnIndex(null));
  });
};

export const selectCSVRowColumnIndex = (row, column) => (dispatch) => {
  batch(() => {
    dispatch(normalizationStateAction.setCsvStartRow(row));
    dispatch(normalizationStateAction.setSelectedColumnIndex(column));
  });
};

export const setAndGeneratePreviewData = () => (dispatch, getState) => {
  const {normalizationState, labwareState} = getState();
  const previewData = generateNormalizationPreviewData(normalizationState, labwareState);

  batch(() => {
    dispatch(normalizationStateAction.setPreviewData(previewData));
  });

  return previewData;
};

export const generateNormalizationTask = (callback) => (dispatch, getState) => {
  dispatch(showLoading('Please wait, system is processing...'));
  const {normalizationState, cardState, temporaryState, configState, protocolState, labwareState} = getState();

  const protocolTrayType = protocolState.lastOpenedProtocol?.trayType;
  const protocolEjectTipLocation = protocolState.lastOpenedProtocol?.isEjectToCaddy ? 1 : 0;
  const maxAspirateVolume =
    protocolTrayType === TrayType.HIGH_VOLUME_CADDY ? configState.maxAspirateHighVolume : configState.maxAspirateVolume;

  const {
    diluentWell: selectedDiluentWell,
    sampleLabwareID,
    type: normalizationType,
    diluentLabwareID,
  } = normalizationState;

  const sampleLabware = find(labwareState, {LabwareID: sampleLabwareID});
  const diluentLabware = find(labwareState, {LabwareID: diluentLabwareID});

  const targetLabware = normalizationType === NormalizationType.FIXED_SAMPLE_VOLUME ? sampleLabware : diluentLabware;

  let {previewData} = normalizationState;

  // trim previewData if the chosed labware is smaller
  const targetLabwareWellCount = targetLabware.rowCount * targetLabware.colCount;
  if (targetLabwareWellCount < previewData.length) {
    const rowsAllowed = _.range(1, targetLabware.rowCount + 1, 1);
    const colsAllowed = _.range(1, targetLabware.colCount + 1, 1);
    let trimmedPreviewData = Object.values(previewData).filter((data) => colsAllowed.includes(data.col));
    trimmedPreviewData = Object.values(trimmedPreviewData).filter((data) => rowsAllowed.includes(data.row));
    dispatch(normalizationStateAction.setPreviewData(trimmedPreviewData));
    previewData = trimmedPreviewData;
  }

  let sourceLabwareID;
  let sourceColumn = 1;
  let sourceRow = 1;

  if (normalizationType === NormalizationType.FIXED_DILUENT_VOLUME) {
    sourceLabwareID = sampleLabware.LabwareID;
    sourceColumn = selectedDiluentWell.column;
    sourceRow = selectedDiluentWell.row;
  } else if (!_.isEmpty(selectedDiluentWell)) {
    sourceLabwareID = diluentLabwareID;
    sourceColumn = selectedDiluentWell.column;
    sourceRow = selectedDiluentWell.row;
  } else if (diluentLabwareID) {
    sourceLabwareID = diluentLabwareID;
  }

  sourceRow = convertRowToAlphabet(sourceRow);

  const {cards, tasks} = cardState;
  const isEditing = temporaryState?.normalizationStateBeforeEdit;
  const selectedCard = find(cards, {isSelected: true});

  let cardID;
  let cardName;

  if (isEditing) {
    cardID = selectedCard.ID;
    cardName = selectedCard.name;
  } else {
    cardID = Object.keys(cards).length === 0 ? 0 : Math.max(...Object.keys(cards)) + 1;
    cardName = getNewCardName(cards, CardType.NORMALIZATION);
  }

  const newTaskID = Object.keys(tasks).length === 0 ? 0 : Math.max(...Object.keys(tasks)) + 1;

  let minTipPositionSourceLabware = dispatch(protocolManagementBusiness.getMinTipPositionByLabwareId(sourceLabwareID));
  let minTipPositionTargetLabware = dispatch(
    protocolManagementBusiness.getMinTipPositionByLabwareId(targetLabware.LabwareID),
  );
  let pipettingParams = {};

  if (isEditing) {
    const editingCards = temporaryState?.normalizationStateBeforeEdit?.cardState.cards;
    const selectedEditingCards = find(editingCards, {isSelected: true});
    const {tasks: editingCardTasks} = selectedEditingCards;
    const editingTaskDetails = Object.values(temporaryState?.normalizationStateBeforeEdit?.cardState?.tasks).filter(
      (task) => editingCardTasks.includes(task.ID),
    );
    const aspirate = editingTaskDetails.find((task) => task.type === TaskType.ASPIRATE);
    const dispense = editingTaskDetails.find((task) => task.type === TaskType.DISPENSE);

    pipettingParams = {
      aspirate,
      dispense,
    };

    const newSourceLabware = find(labwareState, {LabwareID: sourceLabwareID});
    const oldSourceLabware = find(temporaryState.normalizationStateBeforeEdit.labwareState, {
      LabwareID: aspirate.labwareID,
    });
    const oldTargetLabware = find(temporaryState.normalizationStateBeforeEdit.labwareState, {
      LabwareID: dispense.labwareID,
    });

    if (targetLabware.height === oldTargetLabware.height) {
      minTipPositionTargetLabware = dispense.tipPosition;
    }
    if (newSourceLabware.height === oldSourceLabware.height) {
      minTipPositionSourceLabware = aspirate.tipPosition;
    }
  }

  const source = {
    row: sourceRow,
    column: sourceColumn,
    labwareID: sourceLabwareID,
    tipPosition: minTipPositionSourceLabware,
    sourceLabware: find(labwareState, {LabwareID: sourceLabwareID}),
  };

  const target = {
    labwareID: targetLabware.LabwareID,
    tipPosition: minTipPositionTargetLabware,
    targetLabware,
  };

  const params = [
    previewData,
    maxAspirateVolume,
    newTaskID,
    source,
    target,
    normalizationType,
    pipettingParams,
    protocolEjectTipLocation,
  ];

  initiateNormalizationWorker(params, (data) => {
    const newTaskList = data.result;
    const dispenseTask = newTaskList.find((task) => task.type === TaskType.DISPENSE);
    batch(() => {
      if (isEditing) {
        dispatch(temporaryStateAction.removeTemporaryState('normalizationStateBeforeEdit'));
        dispatch(cardStateAction.removeAllCardTask(selectedCard.ID));
      }

      dispatch(cardStateAction.batchAddCardTask(cardID, cardName, CardType.NORMALIZATION, newTaskList));
      dispatch(cardStateAction.saveNormalizationStateToCard(cardID, getState().normalizationState));
      dispatch(smarterDefaultDispenseTipPosition([dispenseTask.labwareID]));
      dispatch(cardStateAction.setCardIsSelected(cardID));
      dispatch(normalizationStateAction.resetNormalizationState());
      dispatch(hideLoading());
      dispatch(protocolManagementBusiness.restoreTipCountForSingleCard(cardID));

      if (callback) {
        callback();
      }
    });
  });
};

export const normalizationBackHandler = () => (dispatch, getState) => {
  const {normalizationState} = getState();
  const {currentWizardView: step} = normalizationState;
  batch(() => {
    switch (step) {
      case NormalizationStep.SET_DILUENT_LABWARE:
        dispatch(normalizationStateAction.resetNormalizationCurrentVolume);
        break;
      case NormalizationStep.UPLOAD_AND_MANAGE_CSV_FILE:
        dispatch(
          normalizationStateAction.restoreNormalizationState({
            ...normalizationState,
            csvData: [],
            wellIncrementPattern: WellIncrementPattern.ORDER_BY_ROW,
            selectedColumnIndex: null,
            delimiter: ',',
            totalRow: 0,
            csvFileName: '',
            csvRawData: '',
          }),
        );
        break;
      case NormalizationStep.SET_TARGET_CONCENTRATION:
        dispatch(
          normalizationStateAction.restoreNormalizationState({
            ...normalizationState,
            targetConcentration: 0,
          }),
        );
        break;
      default:
        break;
    }
  });
};

export const saveNormalizationStateBeforeEdit = () => (dispatch, getState) => {
  const {cardState, labwareState} = getState();
  const {cards} = cardState;
  const selectedCard = find(cards, {isSelected: true});

  // never save task as selected
  const dummyCardState = JSON.parse(JSON.stringify(cardState));
  const taskKeys = Object.keys(dummyCardState.tasks);
  taskKeys.forEach((idx) => {
    dummyCardState.tasks[idx].isSelected = false;
  });
  batch(() => {
    dispatch(normalizationStateAction.restoreNormalizationState(selectedCard.normalizationState));
    dispatch(
      temporaryStateAction.addTemporaryState('normalizationStateBeforeEdit', {
        cardState: dummyCardState,
        labwareState,
      }),
    );
  });
};

export const restoreNormalizationStateAfterCancelEdit = () => (dispatch, getState) => {
  console.log('Restore after cancel edit');
  const {temporaryState} = getState();
  const {cardState, labwareState} = temporaryState.normalizationStateBeforeEdit;
  const {cards, tasks} = cardState;

  dispatch(temporaryStateAction.removeTemporaryState('normalizationStateBeforeEdit'));
  dispatch(cardStateAction.restoreCardState(cards));
  dispatch(cardStateAction.restoreCardTaskState(tasks));
  dispatch(labwareStateAction.restoreLabwareState(labwareState));
  dispatch(normalizationStateAction.resetNormalizationState());
};

export const removeAspirateDispenseTask = (isAspirate) => (dispatch, getState) => {
  const {cardState} = getState();
  const {cards, tasks} = cardState;
  const selectedCard = find(cards, {isSelected: true});

  if (!selectedCard) {
    return;
  }

  let taskToRemove;

  if (isAspirate) {
    taskToRemove = Object.values(tasks).filter(
      (task) => selectedCard.tasks.includes(task.ID) && task.type === TaskType.ASPIRATE,
    );
  } else {
    taskToRemove = Object.values(tasks).filter(
      (task) => selectedCard.tasks.includes(task.ID) && task.type === TaskType.DISPENSE,
    );
  }

  const taskIdsToRemove = taskToRemove.map((task) => task.ID);

  dispatch(cardStateAction.removeCardTasks(selectedCard.ID, taskIdsToRemove));
};

export const normalizationEditStep = (number) => (dispatch, getState) => {
  const {cardState} = getState();
  const {cards} = cardState;
  const selectedCard = find(cards, {isSelected: true});

  if (!selectedCard) {
    return;
  }

  batch(() => {
    dispatch(saveNormalizationStateBeforeEdit());
    switch (number) {
      case 1:
        break;
      case 2:
        dispatch(cardStateAction.removeAllCardTask(selectedCard.ID));
        break;
      case 3:
        break;
      default:
        break;
    }
  });
};

export const isLabwareAllowedForNormalization = (itemID) => (dispatch, getState) => {
  const {labwareState, normalizationState, protocolEditorState} = getState();
  const {type, currentWizardView: step, diluentLabwareID, previewData, sampleLabwareID} = normalizationState;

  const {normalizationTaskEditStep} = protocolEditorState;
  const selectedLabware = find(labwareState, {LabwareID: sampleLabwareID});
  const labware = labwareState[itemID];
  let toastMessage = '';
  let toastParams = {};

  if (labware.isIFC) {
    toastMessage = 'protocolEditor.normalization.createNew.disallowIFCLabware';
    return {toastMessage, toastParams};
  }
  if (labware.isSpearBio) {
    toastMessage = 'protocolEditor.normalization.createNew.disallowSpearBioLabware';
    return {toastMessage, toastParams};
  }
  if (
    (type === NormalizationType.FIXED_DILUENT_VOLUME || step === NormalizationStep.SET_SAMPLE_LABWARE) &&
    (is1WellTrough(labware) || is8WellTrough(labware) || is12WellTrough(labware))
  ) {
    toastMessage = 'protocolEditor.normalization.createNew.disallowTroughLabware';
  } else if (normalizationTaskEditStep === NormalizationStep.SET_SAMPLE_LABWARE) {
    if (diluentLabwareID === labware.LabwareID) {
      toastMessage = 'protocolEditor.normalization.plateAlreadyUsed';
      toastParams = Object.assign(toastParams, {plateUserFor: 'diluent'});
    } else if (
      type === NormalizationType.FIXED_DILUENT_VOLUME &&
      previewData.length > labware.colCount * labware.rowCount
    ) {
      toastMessage = 'protocolEditor.normalization.wrongSamplePlate';
    } else {
      return true;
    }
  } else if (normalizationTaskEditStep === NormalizationStep.SET_DILUENT_LABWARE) {
    if (sampleLabwareID === labware.LabwareID) {
      toastMessage = 'protocolEditor.normalization.plateAlreadyUsed';
      toastParams = Object.assign(toastParams, {plateUserFor: 'sample'});
    } else if (
      type === NormalizationType.FIXED_DILUENT_VOLUME &&
      previewData.length > labware.colCount * labware.rowCount
    ) {
      toastMessage = 'protocolEditor.normalization.wrongPlate';
    }
  } else if (step === NormalizationStep.SET_DILUENT_LABWARE) {
    if (labware.LabwareID === sampleLabwareID) {
      toastMessage = 'protocolEditor.normalization.plateAlreadyUsed';
      toastParams = Object.assign(toastParams, {plateUserFor: 'sample'});
    } else if (type === NormalizationType.FIXED_SAMPLE_VOLUME) {
      return true;
      // eslint-disable-next-line no-unsafe-optional-chaining
    } else if (labware.colCount * labware.rowCount < selectedLabware?.colCount * selectedLabware?.rowCount) {
      toastMessage = 'protocolEditor.normalization.wrongPlate';
    }
  }

  return {toastMessage, toastParams};
};

export const editNormalizationSamplePlate = (labware) => async (dispatch) => {
  dispatch(selectNormalizationSamplePlate(labware));
  await dispatch(setAndGeneratePreviewData());
  dispatch(
    generateNormalizationTask(async () => {
      await dispatch(protocolEditorAction.setNormalizationTaskEditStep(null));
      await dispatch(protocolEditorAction.setSelectedMenu(ProtocolEditorViewMode.SIMPLE_TRANSFER));
      dispatch(globalAddUndoItem());
    }),
  );
};

export const editNormalizationDiluentPlate = (labware) => async (dispatch) => {
  dispatch(selectNormalizationDiluentPlate(labware));
  await dispatch(setAndGeneratePreviewData());
  dispatch(
    generateNormalizationTask(async () => {
      await dispatch(protocolEditorAction.setNormalizationTaskEditStep(null));
      await dispatch(protocolEditorAction.setSelectedMenu(ProtocolEditorViewMode.SIMPLE_TRANSFER));
      dispatch(globalAddUndoItem());
    }),
  );
};

export const showNormalizationInvalidFile = () => (dispatch) => {
  const title = parseMessage('protocolEditor.normalization.modal.invalidFile.title');
  const text = parseMessage('protocolEditor.normalization.modal.invalidFile.text');
  const button = [{label: parseMessage(CommonButtonLabel.GOT_IT)}];

  dispatch(showModalSimplified(title, text, button));
};
